/**
 * @module components/TierTour
 * @description Demo-only "What this tier adds" panel in the demo bar, so each
 * Q1314 Tier 2 line item can be SHOWN on the call in one click instead of
 * being hunted for. Not part of the production page.
 *
 * Every action triggers the real feature (never a mock-up of it):
 *   - Replay loading   → reloads the causes in place (skeletons, then staggered cards)
 *   - Match me         → opens the real guide at question 1, in view and focused
 *   - Guided checkout  → puts two featured causes in the cart and opens checkout
 *   - Saved causes     → explains the persistence (it is invisible by nature)
 */
import { useState, type JSX } from "react";
import { useGiving } from "../app/GivingContext";
import { navigate } from "../app/router";
import { DEFAULT_GIFT_USD } from "../config/page";
import { CURRENT_TIER } from "../config/tier";
import type { Cause } from "../domain/types";
import { OPEN_MATCH_EVENT } from "./MatchMe";
import { Icon } from "./Icons";
import "./TierTour.css";

/** One tour row. No `action` = informational (the feature is used in place). */
interface TourItem {
    title: string;
    description: string;
    actionLabel: string;
    action?: () => void;
}

/** The tour, shown for Enhanced only (Standard is the brief; Immersive is self-evident). */
export function TierTour(): JSX.Element | null {
    const { causes, dispatchCart, replayLoading } = useGiving();
    const [open, setOpen] = useState<boolean>(false);
    if (CURRENT_TIER !== "enhanced") return null;

    /** Seeds the cart with two featured causes, then opens the guided checkout. */
    const tryCheckout = (): void => {
        (causes ?? []).filter((c: Cause) => c.featured).slice(0, 2)
            .forEach((c: Cause) => dispatchCart({ type: "add", causeId: c.id, amountUsd: DEFAULT_GIFT_USD }));
        setOpen(false);
        navigate("checkout");
    };
    const tryMatch = (): void => {
        setOpen(false);
        navigate("browse");
        // Let the browse route mount the guide before asking it to open.
        window.setTimeout(() => window.dispatchEvent(new Event(OPEN_MATCH_EVENT)), 150);
    };
    /** In-page replay: skeletons in view, held a visible beat, then cards stagger in. */
    const replay = (): void => {
        setOpen(false);
        navigate("browse");
        replayLoading();
        window.setTimeout(() => document.querySelector(".skeleton-browse")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    };

    /** The four Q1314 Tier 2 items, as data (one row template renders them all). */
    const items: TourItem[] = [
        { title: "Guided checkout", description: "One question per screen, with progress. Resumes after a reload.", actionLabel: "Show me", action: tryCheckout },
        { title: "Match me to a cause", description: "Three quick questions; every match shows why it fits.", actionLabel: "Show me", action: tryMatch },
        { title: "Motion system", description: "Skeleton loading, page transitions, staggered cards. Off under reduced motion.", actionLabel: "Replay", action: replay },
        { title: "Saved causes remembered", description: "Tap the heart on any card; it stays saved on this device after you leave.", actionLabel: "On every card" },
    ];

    return (
        <div className="tier-tour">
            <button type="button" className="tier-tour__toggle" aria-expanded={open} onClick={() => setOpen((o: boolean) => !o)}>
                <Icon name="sparkle" size={14} /> What Enhanced adds (4)
            </button>
            {open && (
                <div className="tier-tour__panel" role="dialog" aria-label="What Enhanced adds">
                    <header className="tier-tour__head">
                        <p className="tier-tour__title">What Enhanced adds</p>
                        <p className="tier-tour__sub">Everything in Standard, plus these four. Each one runs live.</p>
                    </header>
                    <ol className="tier-tour__list">
                        {items.map((item: TourItem, i: number) => (
                            <li key={item.title} className="tier-tour__item">
                                <span className="tier-tour__num" aria-hidden="true">{i + 1}</span>
                                <span className="tier-tour__text">
                                    <strong className="tier-tour__name">{item.title}</strong>
                                    <span className="tier-tour__desc">{item.description}</span>
                                </span>
                                {item.action !== undefined ? (
                                    <button type="button" className="btn btn--sm btn--ghost tier-tour__action" onClick={item.action}>
                                        {item.actionLabel} <Icon name="arrow-right" size={14} />
                                    </button>
                                ) : (
                                    <span className="tier-tour__note">{item.actionLabel}</span>
                                )}
                            </li>
                        ))}
                    </ol>
                </div>
            )}
        </div>
    );
}
